/**
 * useWorkItem Hook
 *
 * Fetches a single work item and provides mutation helpers with optimistic updates.
 * Used by the Work Item Detail page for viewing and editing work items.
 */

import { createResource, createMemo, Accessor } from "solid-js";
import { api } from "../api";
import type {
  WorkItem,
  WorkItemStatus,
  WorkItemType,
  UpdateWorkItemRequest,
} from "../api";
import { useWebSocket } from "./useWebSocket";
import type { BoardEvent } from "../api/types";

export interface UseWorkItemResult {
  /** The work item data */
  item: Accessor<WorkItem | undefined>;
  /** Available statuses for the project */
  statuses: Accessor<WorkItemStatus[] | undefined>;
  /** Available work item types for the project */
  types: Accessor<WorkItemType[] | undefined>;
  /** Potential parent items (for parent selector) */
  potentialParents: Accessor<WorkItem[] | undefined>;
  /** Child items of the current work item */
  children: Accessor<WorkItem[] | undefined>;
  /** Ancestor chain (root-first), excludes the current item. Empty array when no parent. */
  ancestors: Accessor<WorkItem[] | undefined>;
  /** Whether the item is loading */
  loading: Accessor<boolean>;
  /** Error if fetch failed */
  error: Accessor<Error | undefined>;
  /** Update the work item with optimistic updates */
  update: (patch: UpdateWorkItemRequest) => Promise<void>;
  /** Refetch the work item */
  refetch: () => void;
  /** Refetch all items (updates children and potential parents) */
  refetchAll: () => void;
}

/**
 * Hook for fetching and managing a single work item.
 *
 * @param itemId - Accessor returning the work item ID
 * @param projectId - Accessor returning the project ID
 * @returns Work item data and mutation helpers
 */
export function useWorkItem(
  itemId: Accessor<string>,
  projectId: Accessor<string>,
): UseWorkItemResult {
  const [item, { refetch, mutate }] = createResource(itemId, async (id) => {
    if (!id) return undefined;
    return api.get<WorkItem>(`/work-items/${id}`);
  });

  const [statuses] = createResource(projectId, async (pid) => {
    if (!pid) return undefined;
    return api.get<WorkItemStatus[]>(`/projects/${pid}/statuses`);
  });

  const [types] = createResource(projectId, async (pid) => {
    if (!pid) return undefined;
    return api.get<WorkItemType[]>(`/projects/${pid}/work-items/types`);
  });

  // Fetch potential parent items (for parent selector)
  // Only items with depth < 3 can be parents (max hierarchy is 4 levels: 0-3)
  const [allItems, { refetch: refetchAllItems, mutate: mutateAllItems }] =
    createResource(projectId, async (pid) => {
      if (!pid) return undefined;
      const response = await api.get<{ items: WorkItem[] }>(
        `/projects/${pid}/work-items?limit=500`,
      );
      return response.items;
    });

  // Filter potential parents: must have depth < 3 and not be the current item
  const potentialParents = createMemo(() => {
    const currentId = itemId();
    const items = allItems();
    if (!items) return undefined;
    return items.filter((i) => i.depth < 3 && i.id !== currentId);
  });

  const children = createMemo(() => {
    const currentId = itemId();
    const items = allItems();
    if (!items) return undefined;
    return items.filter((i) => i.parent_id === currentId);
  });

  const MAX_DEPTH = 4;

  // Walk parent_id upward to build a root-first ancestor chain.
  // Returns undefined while data is still loading; [] when the item has no parent.
  const ancestors = createMemo<WorkItem[] | undefined>(() => {
    const current = item();
    const items = allItems();
    if (!current || !items) return undefined;

    const byId = new Map(items.map((i) => [i.id, i]));
    const chain: WorkItem[] = [];
    let cursor = current.parent_id;
    let steps = 0;
    while (cursor && steps < MAX_DEPTH) {
      const next = byId.get(cursor);
      if (!next) break;
      chain.push(next);
      cursor = next.parent_id;
      steps += 1;
    }
    return chain.reverse(); // root-first
  });

  // FLOW-11: keep allItems and the current item in sync with WebSocket
  // events so chat-driven mutations (create/update/status/delete) update
  // children, ancestors, and the page header without a manual refresh.
  useWebSocket({
    onMessage: (event: BoardEvent) => {
      const pid = projectId();
      if (!pid) return;

      switch (event.type) {
        case "WorkItemCreated": {
          if (event.payload.project_id !== pid) return;
          const incoming = event.payload.item;
          mutateAllItems((prev) => {
            if (!prev) return prev;
            if (prev.some((i) => i.id === incoming.id)) return prev;
            return [...prev, incoming];
          });
          break;
        }
        case "WorkItemUpdated": {
          if (event.payload.project_id !== pid) return;
          const incoming = event.payload.item;
          mutateAllItems((prev) =>
            prev?.map((i) => (i.id === incoming.id ? incoming : i)),
          );
          if (incoming.id === itemId()) {
            mutate(incoming);
          }
          break;
        }
        case "WorkItemStatusChanged": {
          if (event.payload.project_id !== pid) return;
          const incoming = event.payload.item;
          mutateAllItems((prev) =>
            prev?.map((i) => (i.id === incoming.id ? incoming : i)),
          );
          if (incoming.id === itemId()) {
            mutate(incoming);
          }
          break;
        }
        case "WorkItemDeleted": {
          if (event.payload.project_id !== pid) return;
          const deletedId = event.payload.item_id;
          mutateAllItems((prev) => prev?.filter((i) => i.id !== deletedId));
          // Intentional: deletion of the current item is not handled here
          // (would require route navigation). Out of scope for FLOW-11.
          break;
        }
      }
    },
  });

  /**
   * Update the work item with optimistic updates and rollback on error.
   * Automatically includes expected_updated_at for optimistic concurrency control.
   */
  const update = async (patch: UpdateWorkItemRequest): Promise<void> => {
    const current = item();
    if (!current) return;

    const optimistic: WorkItem = {
      ...current,
      ...(patch.title !== undefined && { title: patch.title }),
      ...(patch.description !== undefined && {
        description: patch.description ?? undefined,
      }),
      ...(patch.status_id !== undefined && { status_id: patch.status_id }),
      ...(patch.priority !== undefined && { priority: patch.priority }),
      ...(patch.assignee_id !== undefined && {
        assignee_id: patch.assignee_id,
      }),
      ...(patch.remove_assignee && { assignee_id: undefined }),
      ...(patch.parent_id !== undefined && { parent_id: patch.parent_id }),
      ...(patch.remove_parent && { parent_id: undefined }),
      ...(patch.labels !== undefined && { labels: patch.labels }),
      ...(patch.estimate_value !== undefined && {
        estimate_value: patch.estimate_value,
      }),
      ...(patch.estimate_unit !== undefined && {
        estimate_unit: patch.estimate_unit,
      }),
      ...(patch.remove_estimate && {
        estimate_value: null,
        estimate_unit: null,
      }),
    };
    mutate(optimistic);

    try {
      const patchWithConcurrency = {
        ...patch,
        expected_updated_at: current.updated_at,
      };
      const updated = await api.patch<WorkItem>(
        `/work-items/${current.id}`,
        patchWithConcurrency,
      );
      mutate(updated);
    } catch (e) {
      mutate(current);
      throw e;
    }
  };

  return {
    item: () => item(),
    statuses: () => statuses(),
    types: () => types(),
    potentialParents,
    children,
    ancestors,
    loading: () => item.loading,
    error: () => item.error,
    update,
    refetch,
    refetchAll: refetchAllItems,
  };
}
